import { JSX, splitProps } from 'solid-js';
import { mergeRefs, Ref } from '@solid-primitives/refs';

import { cx } from '../utils';

const PaginationAnchorContext = createContext<{
  element: Accessor<HTMLDivElement | undefined>;
}>({
  element: () => undefined,
});

/**
 * 翻页器
 * @param props
 * @returns
 */
export default function Pagination(props: PaginationProps) {
  const anchor = useContext(PaginationAnchorContext);

  const [state, others] = splitProps(props, ['page', 'onChange', 'total', 'pageSize', 'scrollToAnchor']);

  const totalPage = createMemo(() => Math.ceil(state.total / state.pageSize));

  const current = createMemo(() => {
    const currPage = Math.max(state.page, 1);
    const start = (currPage - 1) * state.pageSize + 1;
    const end = state.page * state.pageSize;
    return `${start} - ${Math.min(end, state.total)}`;
  });

  function onPageChange(page: number) {
    if (state.scrollToAnchor && anchor.element()) {
      queueMicrotask(() => {
        anchor.element()?.scrollIntoView({
          // behavior: 'smooth',
          block: 'start',
        });
      });
    }
    state.onChange(page);
  }

  return (
    <Toggle when={totalPage() > 1}>
      {/* hover:bg-alpha-neutral-autowhite-a5 */}
      <div class={cx('flex items-center text-sm  rounded-full font-semibold text-neutral-main1', others.class)}>
        <Button
          class={cx(
            'center w-8 h-8 min-h-8 p-0',
            state.page >= 2 && 'hover:bg-alpha-bw-autowhite-a5 hover:text-brand-default'
          )}
          disabled={state.page < 2}
          onClick={() => onPageChange(state.page - 1)}
        >
          <IconArrow06 class="size-6 rotate-90" />
        </Button>
        <span class="mx-2 text-xs text-center min-w-20">{`${current()} of ${state.total}`}</span>
        <Button
          class={cx(
            'center w-8 h-8 min-h-8 p-0',
            state.page < totalPage() && 'hover:bg-alpha-bw-autowhite-a5 hover:text-brand-default'
          )}
          disabled={state.page >= totalPage()}
          onClick={() => onPageChange(state.page + 1)}
        >
          <IconArrow06 class="size-6 -rotate-90" />
        </Button>
      </div>
    </Toggle>
  );
}

Pagination.Anchor = function (props: ParentProps) {
  const [ref, setRef] = createSignal<HTMLDivElement>();

  return (
    <PaginationAnchorContext.Provider value={{ element: () => ref() }}>
      <Ref ref={mergeRefs((el) => setRef(el as HTMLDivElement))}>{props.children}</Ref>
    </PaginationAnchorContext.Provider>
  );
};

interface PaginationProps extends Omit<JSX.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  // 这里是当前页码
  page: number;
  // 这里是总共多少条数据
  total: number;
  // 这里是每页多少条数据
  pageSize: number;
  onChange(value: number): void;

  scrollToAnchor?: boolean;
}
